import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@medikei/ui';
import { Button } from '@medikei/ui';
import { Calendar, Clock, Video, ArrowRight, Zap, MapPin, Home } from 'lucide-react';
import { schedulingApi } from '@/api';
import type { AppointmentListItem } from '@/api';
import { Skeleton } from '@medikei/ui';
import { ErrorRetry } from '@/components/ui/ErrorRetry';
import { format, differenceInMinutes } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface NextAppointmentCardProps {
  className?: string;
}

export function NextAppointmentCard({ className }: NextAppointmentCardProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [nextAppointment, setNextAppointment] = useState<AppointmentListItem | null>(null);
  const [hasError, setHasError] = useState(false);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setHasError(false);
    setNextAppointment(null);
    try {
      console.log('[NextAppointmentCard] Loading upcoming appointments...');
      const response = await schedulingApi.getUpcomingAppointments(5);
      console.log('[NextAppointmentCard] Response:', response);
      const appointments = response.appointments || [];
      console.log('[NextAppointmentCard] Appointments:', appointments);

      // Filtra estritamente para garantir que apenas consultas futuras ou ativas no momento sejam exibidas como próximas
      const now = new Date();
      const validUpcoming = appointments.filter((apt) => {
        const apptTime = new Date(apt.scheduledAt);
        const diffMinutes = differenceInMinutes(now, apptTime);
        // Se a consulta foi agendada há mais de 30 minutos e não está com chamada ativa, ela já passou
        if (diffMinutes > 30 && apt.status !== 'IN_PROGRESS' && apt.status !== 'DOCTOR_ACCEPTED') {
          return false;
        }
        return true;
      });

      if (validUpcoming.length > 0 && validUpcoming[0]) {
        console.log('[NextAppointmentCard] Setting next appointment:', validUpcoming[0]);
        setNextAppointment(validUpcoming[0]);
      } else {
        console.log('[NextAppointmentCard] No valid future appointments found');
        setNextAppointment(null);
      }
    } catch (error) {
      console.error('[NextAppointmentCard] Failed to load next appointment:', error);
      setHasError(true);
    } finally {
      setIsLoading(false);
      console.log('[NextAppointmentCard] Loading finished');
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const canJoinNow = (appointment: AppointmentListItem): boolean => {
    if (appointment.status !== 'WAITING_DOCTOR' && appointment.status !== 'DOCTOR_ACCEPTED') {
      return false;
    }
    const appointmentTime = new Date(appointment.scheduledAt);
    const now = new Date();
    const diffMinutes = differenceInMinutes(appointmentTime, now);
    return diffMinutes <= 15 && diffMinutes >= -30;
  };

  const getModalityInfo = (appointment: AppointmentListItem) => {
    const isExpress = appointment.type === 'EXPRESS' || appointment.type === 'EMERGENCY';
    const serviceType = appointment.serviceType || 'TELEMEDICINA';

    if (isExpress) {
      return {
        label: 'Telemedicina Expressa (Plantão)',
        badgeClass: 'bg-purple-100 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300 border-purple-200 dark:border-purple-800',
        icon: Zap,
        isTelemed: true,
      };
    }

    if (serviceType === 'PRESENCIAL') {
      return {
        label: 'Consulta Presencial (Consultório)',
        badgeClass: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800',
        icon: MapPin,
        isTelemed: false,
      };
    }

    if (serviceType === 'DOMICILIAR') {
      return {
        label: 'Médico em Casa (Domiciliar)',
        badgeClass: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800',
        icon: Home,
        isTelemed: false,
      };
    }

    return {
      label: 'Telemedicina Agendada (Online)',
      badgeClass: 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300 border-blue-200 dark:border-blue-800',
      icon: Video,
      isTelemed: true,
    };
  };

  const handleJoin = () => {
    if (nextAppointment?.joinUrl) {
      window.location.href = nextAppointment.joinUrl;
    }
  };

  const formatDate = (dateString: string): string => {
    return format(new Date(dateString), "EEEE, dd 'de' MMMM", { locale: ptBR });
  };

  const formatTime = (dateString: string): string => {
    return format(new Date(dateString), 'HH:mm');
  };

  if (isLoading) {
    return (
      <Card className={`p-6 ${className}`}>
        <h3 className="font-semibold text-lg mb-4">Próxima Consulta</h3>
        <Skeleton className="h-24 w-full" />
      </Card>
    );
  }

  if (hasError) {
    return (
      <Card className={`p-6 ${className}`}>
        <h3 className="font-semibold text-lg mb-4">Próxima Consulta</h3>
        <ErrorRetry
          message="Não foi possível carregar sua próxima consulta."
          onRetry={loadData}
          className="py-8"
        />
      </Card>
    );
  }

  if (!nextAppointment) {
    return (
      <Card className={`p-4 sm:p-6 rounded-2xl border border-border ${className}`}>
        <h3 className="font-bold text-lg mb-4 text-foreground">Próxima Consulta</h3>
        <div className="text-center py-6 sm:py-8 text-muted-foreground">
          <Calendar className="w-12 h-12 mx-auto mb-3 text-muted-foreground/40" />
          <p className="text-sm sm:text-base font-medium text-foreground">Nenhuma consulta agendada</p>
          <p className="text-xs text-muted-foreground mt-0.5">Seus próximos atendimentos aparecerão aqui</p>
          <Link to="/agendamentos" className="inline-block mt-4">
            <Button variant="outline" className="rounded-xl font-semibold shadow-xs">
              Agendar consulta
            </Button>
          </Link>
        </div>
      </Card>
    );
  }

  const isJoinable = canJoinNow(nextAppointment);
  const modality = getModalityInfo(nextAppointment);
  const ModalityIcon = modality.icon;

  return (
    <Card className={`p-4 sm:p-6 rounded-2xl border border-border ${className}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="space-y-1">
          <h3 className="font-bold text-lg text-foreground">Próxima Consulta</h3>
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold border shadow-2xs ${modality.badgeClass}`}>
            <ModalityIcon className="w-3.5 h-3.5 shrink-0" />
            <span>{modality.label}</span>
          </span>
        </div>
        {isJoinable && modality.isTelemed && (
          <span className="text-xs font-bold bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 px-2.5 py-1 rounded-full animate-pulse">
            Pode entrar
          </span>
        )}
      </div>

      <div className="flex items-start gap-3.5 sm:gap-4">
        <div className="w-12 h-12 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center overflow-hidden shrink-0 shadow-xs">
          {nextAppointment.doctor?.photoUrl ? (
            <img
              src={nextAppointment.doctor.photoUrl}
              alt={nextAppointment.doctor?.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <span className="text-lg font-bold text-primary">
              {nextAppointment.doctor?.name?.charAt(0) || '?'}
            </span>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <h4 className="font-bold text-sm sm:text-base text-foreground truncate">
            {nextAppointment.doctor?.name || 'Médico não especificado'}
          </h4>
          {nextAppointment.doctor?.specialty && (
            <p className="text-xs sm:text-sm font-medium text-primary truncate mt-0.5">
              {nextAppointment.doctor.specialty}
            </p>
          )}

          <div className="mt-2.5 flex flex-wrap items-center gap-2 sm:gap-3 text-xs sm:text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1.5 bg-muted/40 px-2.5 py-1 rounded-lg border border-border/40 font-medium">
              <Calendar className="w-3.5 h-3.5 text-primary" />
              {formatDate(nextAppointment.scheduledAt)}
            </span>
            <span className="inline-flex items-center gap-1.5 bg-muted/40 px-2.5 py-1 rounded-lg border border-border/40 font-medium">
              <Clock className="w-3.5 h-3.5 text-primary" />
              {formatTime(nextAppointment.scheduledAt)}
            </span>
          </div>
        </div>
      </div>

      <div className="mt-4 pt-3.5 border-t border-border/60 flex flex-col sm:flex-row gap-2">
        {isJoinable && modality.isTelemed ? (
          <Button onClick={handleJoin} className="w-full gap-2 rounded-xl font-bold shadow-md shadow-primary/20">
            <Video className="w-4 h-4" />
            Entrar na Consulta Online
          </Button>
        ) : (
          <Link to={`/agendamentos/${nextAppointment.id}`} className="w-full">
            <Button variant="outline" className="w-full gap-2 rounded-xl font-semibold hover:bg-muted">
              Ver detalhes da consulta
              <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        )}
      </div>
    </Card>
  );
}
